// Pusty prefiks = ten sam adres co strona. Caddy przekierowuje /api/* do backendu.
// Dzięki temu nie trzeba przebudowywać frontendu przy zmianie domeny.
const API = '';

const getToken = () => localStorage.getItem('funhr_token');

const request = async (path, options = {}) => {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API}/api${path}`, { ...options, headers });

  // 401 = wygasła sesja → wyloguj. Wyjątek: samo logowanie — tam 401 to po prostu
  // złe hasło i komunikat ma trafić do formularza, a nie przeładować stronę.
  if (res.status === 401 && path !== '/auth/login') {
    localStorage.removeItem('funhr_token');
    localStorage.removeItem('funhr_user');
    window.location.href = '/login';
    // Nie oddajemy „pustej” odpowiedzi — ekran wywróciłby się na undefined (biała strona), zanim przeglądarka
    // przejdzie do logowania. Zapytanie po prostu czeka na przekierowanie (16.10).
    return new Promise(() => {});
  }

  if (res.status === 413) throw Object.assign(new Error('Zbyt duży załącznik! Maksymalny rozmiar pliku to 5 MB.'), { status: 413 });
  let data = {};
  try { data = await res.json(); } catch { /* odpowiedź nie była JSON-em (np. strona błędu proxy) */ }
  if (!res.ok) {
    const err = new Error(data.error || (res.status >= 500 ? 'Błąd serwera' : 'Nie udało się wykonać operacji'));
    err.status = res.status;   // np. 423 = konto zablokowane (T43)
    err.data = data;
    throw err;
  }
  // Udana zmiana danych → pasek „Czeka na Ciebie" (Layout) od razu przelicza liczby (T89)
  if (options.method && options.method !== 'GET' && !options.quiet) window.dispatchEvent(new Event('funhr:changed'));
  return data;
};

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Nie udało się odczytać pliku.'));
  reader.onload = () => resolve(reader.result);
  reader.readAsDataURL(file);
});
const shrinkImage = (file, maxSide = 1600, quality = 0.8) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Nie udało się odczytać zdjęcia.')); };
  img.onload = () => {
    URL.revokeObjectURL(url);
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    const data = canvas.toDataURL('image/jpeg', quality);
    resolve({ filename: file.name.replace(/\.[^.]+$/, '') + '.jpg', mime: 'image/jpeg', data });
  };
  img.src = url;
});

const api = {
  // Auth
  login: (email, password) =>
    request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  me: () => request('/auth/me'),
  // T171: po zmianie hasła serwer wylogowuje inne urządzenia, a temu oddaje nowy token
  changePassword: (currentPassword, newPassword) =>
    request('/auth/password', { method: 'PUT', body: JSON.stringify({ currentPassword, newPassword }) })
      .then(d => { if (d.token) localStorage.setItem('funhr_token', d.token); return d; }),
  // T171: przedłużenie sesji przy aktywności (token z uprawnieniami ważny 30 min)
  refreshSession: () =>
    request('/auth/refresh', { method: 'POST', quiet: true })
      .then(d => { if (d.token) localStorage.setItem('funhr_token', d.token); return d; }),

  // Users
  getUsers: () => request('/users'),
  createUser: (data) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),
  unlockUser: (id) => request(`/users/${id}/unlock`, { method: 'PUT' }),
  requestProfile: (id) => request(`/users/${id}/request-profile`, { method: 'PUT' }),
  completeProfile: (data) => request('/auth/profile', { method: 'PUT', body: JSON.stringify(data) }),
  updateMyLicense: (data) => request('/auth/license', { method: 'PUT', body: JSON.stringify(data) }),

  // Availability (T57)
  getMyAvailability: (from, to) => request(`/availability/mine?from=${from}&to=${to}`),
  setMyAvailability: (add, remove) => request('/availability/mine', { method: 'PUT', body: JSON.stringify({ add, remove }) }),
  setAvailabilityDetail: (detail) => request('/availability/mine', { method: 'PUT', body: JSON.stringify({ detail }) }), // T67
  getAvailability: (from, to) => request(`/availability?from=${from}&to=${to}`),

  // Messages (T46)
  getInbox: () => request('/messages/inbox'),
  getUnread: () => request('/messages/unread'),
  markRead: (id) => request(`/messages/${id}/read`, { method: 'PUT' }),
  getSentMessages: () => request('/messages/sent'),
  getMessageRecipients: () => request('/messages/recipients'),
  sendMessage: (data) => request('/messages', { method: 'POST', body: JSON.stringify(data) }),

  // Attachments (T60) — plik jako base64; pobranie z tokenem → blob URL
  // Sprawdzenie pliku w momencie wyboru (rozmiar, typ) — zanim cokolwiek wyślemy
  checkFile: (file) => {
    if (!file) return null;
    if (!['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(file.type)) return 'Dozwolone są tylko zdjęcia (JPG, PNG, WEBP) i PDF.';
    if (file.size > 5 * 1024 * 1024) return `Plik ma ${(file.size / 1024 / 1024).toFixed(1)} MB — maksimum to 5 MB. Zrób zdjęcie w niższej rozdzielczości.`;
    return null;
  },
  // T61: zdjęcia zmniejszamy w przeglądarce przed wysłaniem (max 1600 px dłuższy bok, JPEG 0.8) —
  // paragon z telefonu ma 3–5 MB, po zmniejszeniu ~300 KB i nadal jest czytelny. PDF bez zmian.
  fileToBase64: async (file) => {
    const bad = api.checkFile(file);
    if (bad) throw new Error(bad);
    if (file.type.startsWith('image/') && file.size > 300 * 1024) {
      try { return await shrinkImage(file); } catch { /* np. uszkodzony obraz — wyślij oryginał */ }
    }
    return readAsDataUrl(file).then(data => ({ filename: file.name, mime: file.type, data }));
  },
  uploadAttachment: async (ownerType, ownerId, file) => {
    const f = await api.fileToBase64(file);
    return request('/attachments', { method: 'POST', body: JSON.stringify({ ownerType, ownerId, ...f }) });
  },
  openAttachment: async (id) => {
    const res = await fetch(`${API}/api/attachments/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) { let d = {}; try { d = await res.json(); } catch {} throw new Error(d.error || 'Nie udało się pobrać załącznika'); }
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },
  deleteAttachment: (id) => request(`/attachments/${id}`, { method: 'DELETE' }),

  // Documents (T54)
  getMyDocuments: () => request('/documents/mine'),
  submitDocument: (data) => request('/documents', { method: 'POST', body: JSON.stringify(data) }),
  getPendingDocuments: () => request('/documents/pending'),
  approveDocument: (id) => request(`/documents/${id}/approve`, { method: 'PUT' }),
  rejectDocument: (id, note) => request(`/documents/${id}/reject`, { method: 'PUT', body: JSON.stringify({ note }) }),

  // Settings (admin)
  getSettings: () => request('/settings'),
  updateSettings: (data) => request('/settings', { method: 'PUT', body: JSON.stringify(data) }),
  resetUserPassword: (id, newPassword) =>
    request(`/users/${id}/password`, { method: 'PUT', body: JSON.stringify({ newPassword }) }),

  // Leaves
  getLeaves: (params = '') => request(`/leaves${params ? '?' + params : ''}`),
  createLeave: (data) => request('/leaves', { method: 'POST', body: JSON.stringify(data) }),
  approveLeave: (id, comment) =>
    request(`/leaves/${id}/approve`, { method: 'PUT', body: JSON.stringify({ comment }) }),
  rejectLeave: (id, comment) =>
    request(`/leaves/${id}/reject`, { method: 'PUT', body: JSON.stringify({ comment }) }),
  processLeave: (id, note) =>
    request(`/leaves/${id}/process`, { method: 'PUT', body: JSON.stringify({ note }) }),
  updateLeave: (id, data) => request(`/leaves/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  cancelLeave: (id, reason) =>
    request(`/leaves/${id}/cancel`, { method: 'PUT', body: JSON.stringify({ reason }) }),
  getLeaveBalance: (userId, year) => {
    const p = new URLSearchParams();
    if (userId) p.set('userId', userId);
    if (year) p.set('year', year);
    const q = p.toString();
    return request(`/leaves/balance${q ? '?' + q : ''}`);
  },
  getWorkdays: (from, to) => request(`/leaves/workdays?from=${from}&to=${to}`),

  // Hours
  getHours: (params = '') => request(`/hours${params ? '?' + params : ''}`),
  createHours: (data) => request('/hours', { method: 'POST', body: JSON.stringify(data) }),
  updateHours: (id, data) => request(`/hours/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteHours: (id) => request(`/hours/${id}`, { method: 'DELETE' }),
  approveHours: (id) => request(`/hours/${id}/approve`, { method: 'PUT' }),
  getMonthSummary: (month, userId) => request(`/hours/month-summary?month=${month}${userId ? '&userId=' + userId : ''}`), // T98
  unapproveHours: (id) => request(`/hours/${id}/unapprove`, { method: 'PUT' }),   // T95: cofnij zatwierdzenie
  unapproveBonus: (id) => request(`/bonuses/${id}/unapprove`, { method: 'PUT' }),
  rejectHours: (id, reason) => request(`/hours/${id}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) }), // T148: powód widzi autor
  approveBulkHours: (ids) =>
    request('/hours/approve-bulk', { method: 'PUT', body: JSON.stringify({ ids }) }),
  deleteBulkHours: (ids) => request('/hours/delete-bulk', { method: 'PUT', body: JSON.stringify({ ids }) }),
  approveBulkBonuses: (ids) => request('/bonuses/approve-bulk', { method: 'PUT', body: JSON.stringify({ ids }) }),
  deleteBulkBonuses: (ids) => request('/bonuses/delete-bulk', { method: 'PUT', body: JSON.stringify({ ids }) }),

  // Bonuses
  getBonuses: (params = '') => request(`/bonuses${params ? '?' + params : ''}`),
  createBonus: (data) => request('/bonuses', { method: 'POST', body: JSON.stringify(data) }),
  approveBonus: (id) => request(`/bonuses/${id}/approve`, { method: 'PUT' }),
  rejectBonus: (id, reason) => request(`/bonuses/${id}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) }),
  updateBonus: (id, data) => request(`/bonuses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteBonus: (id) => request(`/bonuses/${id}`, { method: 'DELETE' }),

  // Events
  getEvents: (params = '') => request(`/events${params ? '?' + params : ''}`),
  getDmuchappStatus: () => request('/events/dmuchapp'),                       // T156
  syncDmuchapp: () => request('/events/dmuchapp/sync', { method: 'POST' }),
  getEvent: (id) => request(`/events/${id}`),
  createEvent: (data) => request('/events', { method: 'POST', body: JSON.stringify(data) }),
  updateEvent: (id, data) => request(`/events/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateEventStatus: (id, status) => request(`/events/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  deleteEvent: (id) => request(`/events/${id}`, { method: 'DELETE' }),
  getMyUnsettledEvents: () => request('/events/my/unsettled'),               // T66
  getEventCandidates: (date) => request(`/events/candidates${date ? '?date=' + date : ''}`), // T58

  // Settlements / wypłaty (T87)
  getMySettlements: () => request('/settlements/mine'),
  getPendingCounts: () => request('/pending'), // T89: pasek „czeka na Ciebie"
  getSettlementSummary: () => request('/settlements/summary'),
  previewSettlement: (userIds, upToDate) => request('/settlements/preview', { method: 'POST', body: JSON.stringify({ userIds, upToDate }) }),
  createSettlement: (userIds, upToDate, note, methods) => request('/settlements', { method: 'POST', body: JSON.stringify({ userIds, upToDate, note, methods }) }),
  setSettlementMethod: (id, payMethod) => request(`/settlements/${id}/method`, { method: 'PUT', body: JSON.stringify({ payMethod }) }), // T116
  getStaff: () => request('/users/staff'), // T113
  getMyTeam: () => request('/users/team'), // T124
  getAbsences: (from, to) => request(`/leaves/calendar?from=${from}&to=${to}`), // T127
  setLeavePool: (id, leaveDaysTotal) => request(`/users/${id}/leave-pool`, { method: 'PUT', body: JSON.stringify({ leaveDaysTotal }) }), // T113
  getSettlementHistory: (userId) => request(`/settlements/history${userId ? '?userId=' + userId : ''}`),
  revertSettlement: (id) => request(`/settlements/${id}`, { method: 'DELETE' }),
  getSettlement: (id) => request(`/settlements/${id}`),                                  // T90: specyfikacja „za co"
  getPersonEvents: (userId, params = '') => request(`/users/${userId}/events${params ? '?' + params : ''}`), // T91
  getTeamChanges: (all) => request(`/events/changes${all ? '?all=1' : ''}`), // T104
  markTeamChangesSeen: (ids) => request('/events/changes/seen', { method: 'PUT', body: JSON.stringify({ ids }) }),
  confirmEvent: (id) => request(`/events/${id}/confirm`, { method: 'PUT' }), // T64
  declineEvent: (id, reason) => request(`/events/${id}/decline`, { method: 'PUT', body: JSON.stringify({ reason }) }),
  updateEventNote: (id, noteId, data) => request(`/events/${id}/notes/${noteId}`, { method: 'PUT', body: JSON.stringify(data) }), // T166
  addEventNote: (id, data) =>
    request(`/events/${id}/notes`, { method: 'POST', body: JSON.stringify(data) }),

  // Supplies
  getSupplies: (params = '') => request(`/supplies${params ? '?' + params : ''}`),
  createSupply: (data) => request('/supplies', { method: 'POST', body: JSON.stringify(data) }),
  approveSupply: (id) => request(`/supplies/${id}/approve`, { method: 'PUT' }),
  // T110
  updateSupply: (id, data) => request(`/supplies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  withdrawSupply: (id) => request(`/supplies/${id}`, { method: 'DELETE' }),
  rejectSupply: (id, reason) => request(`/supplies/${id}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) }),
  orderSupply: (id, data) => request(`/supplies/${id}/order`, { method: 'PUT', body: JSON.stringify(data) }),
  deliverSupply: (id) => request(`/supplies/${id}/deliver`, { method: 'PUT' }),
  updateSupplyStatus: (id, status) =>
    request(`/supplies/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),

  // Reports
  getWeeklyReport: (params = '') => request(`/reports/weekly${params ? '?' + params : ''}`),
  getDocumentsReport: () => request('/reports/documents'),
  getLeaveBalances: () => request('/reports/leave-balances'),
  getRemoteReport: (month) => request(`/leaves/remote-report?month=${month}`), // T97: ewidencja pracy zdalnej
  getAuditLog: (params = '') => request(`/reports/audit${params ? '?' + params : ''}`),
  getLoginLog: (params = '') => request(`/reports/logins${params ? '?' + params : ''}`), // T172
};

export default api;
